/**
 * 数字员工项目（任务）类型与列表/创建 payload 辅助
 */
import { getUser } from '@/utils/auth'

/** 项目类型编码：电商 i1、通用 i2、视频 v1 */
export const DE_PROJECT_TYPE = {
  ecom: { code: 'i1', label: '电商生图' },
  general: { code: 'i2', label: '通用生图' },
  video: { code: 'v1', label: '视频生成' },
}

const ACTIVE_PROJECT_KEY_PREFIX = 'deu-active-project-'

export function getUserApiKey() {
  const user = getUser()
  return (user && user.base && user.base.apiKey) ? String(user.base.apiKey).trim() : ''
}

export function isDeApiSuccess(res) {
  if (!res) return false
  if (res.success === false) return false
  if (res.success === true) return true
  return res.code === 0 || res.code === '0'
}

export function getDeApiErrorMessage(res, fallback = '操作失败') {
  if (!res) return fallback
  if (typeof res === 'string') return res.trim() || fallback
  if (res.error && typeof res.error === 'object') {
    const nested = res.error.message || res.error.msg
    if (nested) return String(nested)
  }
  if (res.error && typeof res.error === 'string') return res.error
  return res.msg || res.message || fallback
}

/** 积分不足时展示给用户的统一文案 */
export const INSUFFICIENT_CREDITS_MSG = '积分不足，请重新生成！'

export function isInsufficientCreditsMessage(text) {
  if (text == null || text === '') return false
  return /积分不足|积分不够|余额不足|insufficient\s*(credits?|points?|balance)/i.test(String(text))
}

export function isInsufficientCreditsResponse(res) {
  return isInsufficientCreditsMessage(getDeApiErrorMessage(res, ''))
}

export function createInsufficientCreditsError() {
  const err = new Error(INSUFFICIENT_CREDITS_MSG)
  err.code = 'INSUFFICIENT_CREDITS'
  return err
}

export function isInsufficientCreditsError(err) {
  if (!err) return false
  if (err.code === 'INSUFFICIENT_CREDITS') return true
  if (isInsufficientCreditsMessage(err.message)) return true
  if (err.data && isInsufficientCreditsResponse(err.data)) return true
  return false
}

/** 工作流单步失败文案（积分不足优先） */
export function resolveWorkflowModelItemError(err, fallback = '生成失败') {
  if (isInsufficientCreditsError(err)) return INSUFFICIENT_CREDITS_MSG
  return resolveWorkflowErrorMessage(err, fallback)
}

/** 从接口响应或 Error 对象提取可读错误文案 */
export function resolveWorkflowErrorMessage(err, fallback = '操作失败') {
  if (!err) return fallback
  if (typeof err === 'string') return err.trim() || fallback
  if (err.data) return getDeApiErrorMessage(err.data, fallback)
  if (err.message && err.message !== '[object Object]') return String(err.message)
  return fallback
}

export function activeProjectStorageKey(navType) {
  return `${ACTIVE_PROJECT_KEY_PREFIX}${navType}`
}

export function stashActiveProjectId(navType, projectId) {
  if (projectId == null || projectId === '') return
  try {
    sessionStorage.setItem(activeProjectStorageKey(navType), String(projectId))
  } catch (_) { /* quota */ }
}

export function clearActiveProjectId(navType) {
  try {
    sessionStorage.removeItem(activeProjectStorageKey(navType))
  } catch (_) { /* ignore */ }
}

/** 读取当前任务 ID（刷新后仍可恢复，不删除） */
export function getActiveProjectId(navType) {
  try {
    return sessionStorage.getItem(activeProjectStorageKey(navType))
  } catch (_) {
    return null
  }
}

/** 读取并清除（仅用于一次性消费场景，工作流列表加载请用 getActiveProjectId） */
export function takeActiveProjectId(navType) {
  try {
    const key = activeProjectStorageKey(navType)
    const id = sessionStorage.getItem(key)
    if (id) sessionStorage.removeItem(key)
    return id
  } catch (_) {
    return null
  }
}

/**
 * 解析应打开的项目 ID：sessionStorage > 路由 query > 草稿
 * @param {object} [routeQuery]
 */
export function resolvePreferredProjectId(navType, routeQuery, draft) {
  const fromStorage = getActiveProjectId(navType)
  if (fromStorage) return fromStorage
  const q = routeQuery || {}
  const fromRoute = q.projectId || q.project
  if (fromRoute != null && fromRoute !== '') return fromRoute
  if (draft && draft.projectId != null && draft.projectId !== '') {
    return draft.projectId
  }
  return null
}

/**
 * 构建项目列表查询参数（GET /ai_employee/projects）
 * @param {'ecom'|'general'|'video'} navType
 */
export function buildProjectListQueryParams(navType, { page = 1, page_size = 20 } = {}) {
  const meta = DE_PROJECT_TYPE[navType] || DE_PROJECT_TYPE.ecom
  return {
    page,
    page_size,
    project_type: meta.code,
  }
}

/**
 * 构建创建项目请求体
 * @param {'ecom'|'general'|'video'} navType
 */
export function buildCreateProjectPayload(navType, { text = '', images = [], videoParams } = {}) {
  const meta = DE_PROJECT_TYPE[navType] || DE_PROJECT_TYPE.ecom
  const trimmed = String(text || '').trim()
  const firstLine = trimmed.split('\n')[0].trim()
  const projectName = (firstLine || `新任务 · ${meta.label}`).slice(0, 48)
  const referenceImages = (images || [])
    .map((img) => (typeof img === 'string' ? img : img && img.src))
    .filter(Boolean)

  const payload = {
    projectName,
    projectType: meta.code,
    prompt: trimmed,
  }

  if (referenceImages.length) {
    payload.referenceImages = referenceImages
  }

  const metadata = {}
  if (navType === 'video' && videoParams) {
    const ratioRaw = videoParams.ratio || ''
    metadata.aspectRatio = String(ratioRaw).replace(/^横屏\s*/i, '').trim() || ratioRaw
    if (videoParams.model) metadata.model = videoParams.model
    if (videoParams.duration) metadata.duration = videoParams.duration
    if (videoParams.resolution) metadata.resolution = videoParams.resolution
    if (videoParams.channel) metadata.channel = videoParams.channel
    if (videoParams.quality) metadata.quality = videoParams.quality
  }

  if (Object.keys(metadata).length) {
    payload.metadata = metadata
  }

  return payload
}

const EWF_HISTORY_COLORS = ['a', 'b', 'c']
const VWF_HISTORY_COLORS = ['#8b5cf6', '#7c3aed', '#6d28d9']

export function formatProjectListTime(dateLike) {
  if (!dateLike) return '刚刚'
  const d = new Date(dateLike)
  if (Number.isNaN(d.getTime())) return String(dateLike)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.floor((startOfToday - startOfDate) / 86400000)
  if (diffDays === 0) return '今天'
  if (diffDays === 1) return '昨天'
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}-${dd}`
}

/**
 * 将接口项目项映射为侧栏历史任务
 */
export function mapProjectItemToHistoryTask(item, index, navType) {
  const isVideo = navType === 'video'
  const colors = isVideo ? VWF_HISTORY_COLORS : EWF_HISTORY_COLORS
  const refs = Array.isArray(item.referenceImages)
    ? item.referenceImages
    : (item.referenceImages ? [item.referenceImages] : [])

  return {
    id: item.id,
    title: item.projectName || '未命名项目',
    time: formatProjectListTime(item.updatedAt || item.createdAt),
    step: 0,
    color: colors[index % colors.length],
    status: item.status,
    prompt: item.prompt || '',
    referenceImages: refs.filter(Boolean),
    metadata: item.metadata || null,
    projectType: item.projectType,
  }
}

/** 任务管理列表状态：idle | running | done */
export function mapProjectStatusToDeuTask(status) {
  const v = String(status || '').toLowerCase()
  if (v === 'completed' || v === 'done' || v === 'success') return 'done'
  if (v === 'running' || v === 'processing' || v === 'in_progress') return 'running'
  return 'idle'
}

/**
 * 数字员工使用页「任务管理」列表项
 */
export function mapProjectItemToDeuTask(item) {
  return {
    id: item.id,
    name: item.projectName || '未命名任务',
    status: mapProjectStatusToDeuTask(item.status),
    time: formatProjectListTime(item.updatedAt || item.createdAt),
    prompt: item.prompt || '',
    projectType: item.projectType,
    rawStatus: item.status,
  }
}

export function resolveActiveTaskId(tasks, preferredId) {
  if (preferredId != null && preferredId !== '') {
    if (tasks && tasks.length) {
      const hit = tasks.find((t) => String(t.id) === String(preferredId))
      if (hit) return hit.id
    }
    return preferredId
  }
  if (!tasks || !tasks.length) return null
  return tasks[0].id
}
